"use client"

import { useRef, useEffect, useState, memo } from "react"
import { Check, Loader2, Circle, ChevronRight } from "lucide-react"
import type { SubAgent } from "@/lib/agent-demo-data"
import { cn } from "@/lib/utils"
import "./sub-agent-card.less"

function getCurrentAction(agent: SubAgent): string {
  if (agent.status === "done") return ""
  for (const skill of agent.skills) {
    if (skill.status === "running") {
      for (const tc of skill.toolCalls) {
        if (tc.status === "running") return tc.label
        if (tc.status === "waiting") return tc.paramLabel || "等待回复..."
      }
      return skill.name
    }
  }
  return "准备中..."
}

function getProgress(agent: SubAgent): number {
  const total = agent.skills.reduce((a, s) => a + s.toolCalls.length, 0)
  if (total === 0) return 0
  const done = agent.skills.reduce((a, s) => a + s.toolCalls.filter(t => t.status === "done").length, 0)
  return Math.round((done / total) * 100)
}

function SubAgentCardInner({
  agent,
  roleIcon,
  isSelected,
  onClick,
}: {
  agent: SubAgent
  roleIcon?: React.ReactNode
  isSelected: boolean
  onClick: () => void
}) {
  const currentAction = getCurrentAction(agent)
  const isDone = agent.status === "done"
  const isRunning = agent.status === "running"
  const isPending = agent.status === "pending"
  const progress = getProgress(agent)

  // Smooth text transition: fade out old → fade in new
  const [displayAction, setDisplayAction] = useState(currentAction)
  const [transitioning, setTransitioning] = useState(false)
  const prevActionRef = useRef(currentAction)

  useEffect(() => {
    if (currentAction === prevActionRef.current) return
    prevActionRef.current = currentAction
    setTransitioning(true)
    const timer = setTimeout(() => {
      setDisplayAction(currentAction)
      setTransitioning(false)
    }, 150)
    return () => clearTimeout(timer)
  }, [currentAction])

  return (
    <button
      onClick={onClick}
      className={cn(
        "sub-agent-card w-full max-w-md text-left rounded-xl border px-3 py-2 transition-all duration-200 group flex items-center gap-2",
        isSelected
          ? "sub-agent-card--selected border-primary/30 bg-primary/5"
          : isDone
            ? "border-primary/20 bg-card/80 hover:bg-secondary/30"
            : isPending
              ? "border-border/30 bg-card/50 opacity-60"
              : "border-border/40 bg-card/70 hover:bg-secondary/30 hover:border-border/60",
      )}
    >
      {/* Status indicator */}
      <div className="shrink-0 flex items-center justify-center w-4 h-4">
        {isDone ? (
          <div className="flex items-center justify-center w-4 h-4 rounded-full bg-primary/15">
            <Check className="h-2.5 w-2.5 text-primary" strokeWidth={3} />
          </div>
        ) : isRunning ? (
          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
        ) : (
          <Circle className="h-2 w-2 text-muted-foreground/30 fill-muted-foreground/10" />
        )}
      </div>

      {/* Role icon */}
      <span className={cn(
        "shrink-0 transition-colors duration-200",
        isDone ? "text-primary" : isRunning ? "text-primary" : "text-muted-foreground/40",
      )}>
        {roleIcon || <Circle className="h-3.5 w-3.5" />}
      </span>

      {/* Name */}
      <span className={cn(
        "text-xs font-medium truncate shrink-0 transition-colors duration-200",
        isDone ? "text-muted-foreground" : isPending ? "text-muted-foreground/50" : "text-foreground",
      )}>
        {agent.name}
      </span>

      {/* Divider + action / progress */}
      {isRunning && displayAction ? (
        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
          <span className="text-muted-foreground/20 text-micro shrink-0">·</span>
          <span
            className={cn(
              "sub-agent-action-text text-caption text-muted-foreground truncate",
              transitioning ? "sub-agent-text-out" : "sub-agent-text-in",
            )}
          >
            {displayAction}
          </span>
        </div>
      ) : isDone ? (
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="text-muted-foreground/20 text-micro shrink-0">·</span>
          <span className="text-micro text-primary/60 font-medium">完成</span>
        </div>
      ) : isPending ? (
        <div className="flex-1 min-w-0">
          <span className="text-micro text-muted-foreground/30 ml-1">等待中</span>
        </div>
      ) : (
        <span className="flex-1" />
      )}

      {/* Running: micro progress bar */}
      {isRunning && (
        <div className="shrink-0 w-8 h-1 rounded-full bg-primary/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary/50 transition-all duration-700 ease-out"
            style={{ width: `${Math.max(progress, 8)}%` }}
          />
        </div>
      )}

      <ChevronRight className={cn(
        "h-3 w-3 shrink-0 transition-all duration-200",
        isSelected ? "text-primary/50" : "text-muted-foreground/20 group-hover:text-muted-foreground/50",
      )} />
    </button>
  )
}

export const SubAgentCard = memo(SubAgentCardInner)
